import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  Sparkles, 
  ZoomIn, 
  Download, 
  Loader2,
  ChevronDown,
  Check,
  SplitSquareHorizontal,
  Wand2
} from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';
import DownloadFormatModal from './DownloadFormatModal';

const ImageActions = ({ 
  imageUrl, 
  onImageUpdate, 
  originalPrompt = null,  // Original prompt for "Make It Better"
  imageStyle = "product", // Style for regeneration
  showEnhance = true,
  showUpscale = true,
  showDownload = true,
  showCompare = true,
  showMakeItBetter = true,
  className = "",
  // Engagement loop callbacks
  onMakeItBetter: onMakeItBetterCallback,
  onEnhance: onEnhanceCallback,
  onUpscale: onUpscaleCallback
}) => {
  const { api, user, refreshUser } = useAuth();
  const [enhancing, setEnhancing] = useState(false);
  const [upscaling, setUpscaling] = useState(false);
  const [makingBetter, setMakingBetter] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [currentScale, setCurrentScale] = useState(null);
  const [improvedPrompt, setImprovedPrompt] = useState(null);
  
  // Before/After comparison state
  const [originalImage, setOriginalImage] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [autoShowComparison, setAutoShowComparison] = useState(false);
  
  // Download format modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  
  // Track if any modification has been made
  const hasModification = isEnhanced || currentScale !== null || improvedPrompt !== null;

  // Store original image when component mounts or imageUrl changes (and no modification yet)
  useEffect(() => {
    if (imageUrl && !originalImage && !hasModification) {
      setOriginalImage(imageUrl);
    }
  }, [imageUrl, originalImage, hasModification]);

  // Reset state when a completely new image is provided (e.g., new generation)
  const prevImageRef = useRef(imageUrl);
  useEffect(() => {
    // If the imageUrl changed and we haven't done any modifications yet,
    // this is likely a new generation - reset everything
    if (imageUrl !== prevImageRef.current && !hasModification) {
      setOriginalImage(imageUrl);
      setIsEnhanced(false);
      setCurrentScale(null);
      setImprovedPrompt(null);
    }
    prevImageRef.current = imageUrl;
  }, [imageUrl, hasModification]);

  const handleEnhance = async () => {
    if (!imageUrl) return;
    
    // Store original before first modification
    if (!originalImage) {
      setOriginalImage(imageUrl);
    }
    
    setEnhancing(true);
    try {
      const response = await api.post('/ai/enhance-image', {
        image_url: imageUrl
      });
      
      if (response.data.enhanced_url) {
        onImageUpdate?.(response.data.enhanced_url);
        setIsEnhanced(true);
        toast.success('Image enhanced!');
        
        // Track engagement loop step
        if (onEnhanceCallback) onEnhanceCallback();
        
        // Auto-show comparison briefly
        setAutoShowComparison(true);
        setShowComparison(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Enhancement failed';
      toast.error(message);
    } finally {
      setEnhancing(false);
    }
  };

  const handleUpscale = async (scale) => {
    if (!imageUrl) return;
    
    const cost = scale === 2 ? 2 : 4;
    const isAdmin = user?.is_admin; // Admin testing mode - bypass credit checks
    
    if (!isAdmin && (user?.credits || 0) < cost) {
      toast.error(`Insufficient credits. Upscale ${scale}x requires ${cost} credits.`);
      return;
    }
    
    // Store original before first modification
    if (!originalImage) {
      setOriginalImage(imageUrl);
    }
    
    setUpscaling(true);
    try {
      const response = await api.post('/ai/upscale-image', {
        image_url: imageUrl,
        scale: scale
      });
      
      if (response.data.upscaled_url) {
        onImageUpdate?.(response.data.upscaled_url);
        setCurrentScale(scale);
        await refreshUser();
        toast.success(`Image upscaled ${scale}x! (${cost} credits used)`);
        
        // Track engagement loop step
        if (onUpscaleCallback) onUpscaleCallback();
        
        // Auto-show comparison briefly
        setAutoShowComparison(true);
        setShowComparison(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Upscale failed';
      toast.error(message);
    } finally {
      setUpscaling(false);
    }
  };

  const handleMakeItBetter = async () => {
    if (!originalPrompt) {
      toast.error('No prompt available for improvement');
      return;
    }
    
    const cost = 3;  // Make It Better → 3 credits
    const isAdmin = user?.is_admin; // Admin testing mode - bypass credit checks
    
    if (!isAdmin && (user?.credits || 0) < cost) {
      toast.error(`Insufficient credits. Make It Better requires ${cost} credits.`);
      return;
    }
    
    // Store original before modification
    if (!originalImage) {
      setOriginalImage(imageUrl);
    }
    
    setMakingBetter(true);
    
    try {
      // Extract base64 from image URL for refinement
      let imageBase64 = null;
      if (imageUrl) {
        if (imageUrl.includes('base64,')) {
          imageBase64 = imageUrl.split('base64,')[1];
        } else if (imageUrl.startsWith('data:')) {
          imageBase64 = imageUrl.split(',')[1];
        }
      }
      
      const response = await api.post('/ai/make-it-better', {
        original_prompt: originalPrompt,
        style: imageStyle,
        // Pass reference image for true image-to-image refinement
        reference_image_base64: imageBase64,
        preserve_composition: true,
        refinement_strength: 0.45
      });
      
      if (response.data.success) {
        // Use image_base64 if available, otherwise image_url
        const newImageUrl = response.data.image_base64 
          ? `data:image/png;base64,${response.data.image_base64}`
          : response.data.image_url;
        
        onImageUpdate?.(newImageUrl);
        setImprovedPrompt(response.data.improved_prompt);
        setIsEnhanced(true); // The result is already enhanced
        await refreshUser();
        
        // Track engagement loop step
        if (onMakeItBetterCallback) onMakeItBetterCallback();
        
        // Show different message based on refinement mode
        const refinementUsed = response.data.refinement_used;
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">{refinementUsed ? 'Image refined!' : 'Design improved!'}</p>
            <p className="text-xs text-muted-foreground">
              {refinementUsed ? 'Structure preserved • 3 credits used' : '3 credits used'}
            </p>
          </div>
        );
        
        // Auto-show comparison
        setAutoShowComparison(true);
        setShowComparison(true);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to improve design';
      toast.error(message);
    } finally {
      setMakingBetter(false);
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    setShowDownloadModal(true);
  };

  const handleCompareClick = () => {
    setAutoShowComparison(false);
    setShowComparison(true);
  };

  const handleCloseComparison = () => {
    setShowComparison(false);
    setAutoShowComparison(false);
  };

  const isProcessing = enhancing || upscaling || makingBetter;

  if (!imageUrl) return null;

  return (
    <>
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        {/* Badges */}
        <div className="flex items-center gap-1.5">
          {/* Enhanced badge */}
          {isEnhanced && (
            <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" />
              Enhanced
            </span>
          )}
          
          {/* Upscaled badge */}
          {currentScale && (
            <span className="text-xs bg-blue-500/10 text-blue-600 px-2 py-1 rounded-full flex items-center gap-1">
              <ZoomIn className="w-3 h-3" />
              {currentScale}x
            </span>
          )}
          
          {/* Improved badge */}
          {improvedPrompt && (
            <span className="text-xs bg-purple-500/10 text-purple-600 px-2 py-1 rounded-full flex items-center gap-1">
              <Wand2 className="w-3 h-3" />
              Improved
            </span>
          )}
        </div>

        {/* Make It Better Button - Primary action (always show if there's a prompt) */}
        {showMakeItBetter && originalPrompt && (
          <Button
            size="sm"
            onClick={handleMakeItBetter}
            disabled={isProcessing}
            className={`h-8 border-0 ${
              improvedPrompt 
                ? 'bg-gradient-to-r from-violet-500/80 to-purple-500/80 hover:from-violet-600/80 hover:to-purple-600/80' 
                : 'bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-md shadow-purple-500/20'
            } text-white`}
            data-testid="make-it-better-btn"
          >
            {makingBetter ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                <span className="hidden sm:inline">Refining your design...</span>
                <span className="sm:hidden">Refining...</span>
              </>
            ) : (
              <>
                <Wand2 className="w-3 h-3 mr-1.5" />
                <span className="hidden sm:inline">{improvedPrompt ? 'Make It Even Better' : 'Make It Better'}</span>
                <span className="sm:hidden">Better</span>
                <span className="ml-1 text-xs opacity-75 hidden sm:inline">(3 cr)</span>
              </>
            )}
          </Button>
        )}

        {/* Compare Button - Only show if modifications have been made */}
        {showCompare && hasModification && originalImage && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCompareClick}
            disabled={isProcessing}
            className="h-8 border-accent/50 hover:border-accent hover:bg-accent/5"
            data-testid="compare-btn"
          >
            <SplitSquareHorizontal className="w-3 h-3 mr-1" />
            Compare
          </Button>
        )}

        {/* Enhance Button */}
        {showEnhance && !isEnhanced && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEnhance}
            disabled={isProcessing}
            className="h-8"
            data-testid="enhance-btn"
          >
            {enhancing ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Enhancing...
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 mr-1" />
                Enhance
              </>
            )}
          </Button>
        )}

        {/* Upscale Dropdown */}
        {showUpscale && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isProcessing}
                className="h-8"
                data-testid="upscale-btn"
              >
                {upscaling ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Upscaling...
                  </>
                ) : (
                  <>
                    <ZoomIn className="w-3 h-3 mr-1" />
                    Upscale
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => handleUpscale(2)}
                disabled={upscaling}
                className="cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span>2× Upscale</span>
                  <span className="text-xs text-muted-foreground ml-4">2 credits</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleUpscale(4)}
                disabled={upscaling}
                className="cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span>4× Upscale</span>
                  <span className="text-xs text-muted-foreground ml-4">4 credits</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Your credits: {user?.credits || 0}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Download Button */}
        {showDownload && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isProcessing}
            className="h-8"
            data-testid="download-btn"
          >
            <Download className="w-3 h-3 mr-1" />
            Download
          </Button>
        )}
      </div>

      {/* Before/After Comparison Modal */}
      <BeforeAfterSlider
        beforeImage={originalImage}
        afterImage={imageUrl}
        isOpen={showComparison}
        onClose={handleCloseComparison}
        autoShow={autoShowComparison}
        autoShowDuration={2500}
      />
      
      {/* Download Format Modal */}
      <DownloadFormatModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        imageUrl={imageUrl}
        defaultFilename="mockuppro-image"
      />
    </>
  );
};

export default ImageActions;
